import i18n from '@assets/i18n/config';
import type { EntityTypes, RelatedEntityTypes, User } from '@interfaces/user';
import { getRelatedEntityTypes } from '@interfaces/user';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { ClientSelectField } from '@ovComponents/2-component/clientSelectField/clientSelect';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

type RelatedEntitySelectProps = {
  forUserType: EntityTypes;
  entitySelectLabel: string;
  relationSelectLabel: string;
  entity?: Partial<User>;
  relation?: RelatedEntityTypes;
  onEntityChange?: (entity?: User) => void;
  onRelationChange?: (relation?: RelatedEntityTypes) => void;
};

type RelatedEntityData = {
  entity?: Partial<User>;
  relation?: RelatedEntityTypes;
};

export const RelatedEntitySelect = (props: RelatedEntitySelectProps) => {
  const { t } = useTranslation(['relatedEntityTypes']);
  const [relatedEntityData, setRelatedEntityData] = useState<RelatedEntityData>(
    { entity: props.entity, relation: props.relation },
  );
  const candidateRelations: RelatedEntityTypes[] = getRelatedEntityTypes(
    props.forUserType,
  );

  const handleEntityChange = (entity?: User) => {
    setRelatedEntityData((prevState) => {
      const relation = entity ? prevState?.relation : undefined;
      props.onRelationChange?.(relation);
      props.onEntityChange?.(entity);
      return { entity, relation };
    });
  };

  const handleRelationChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const relation = e.target.value as RelatedEntityTypes;
    setRelatedEntityData({ ...relatedEntityData, relation });
    props.onRelationChange?.(relation);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <ClientSelectField
          user={relatedEntityData.entity}
          setUser={handleEntityChange}
          label={props.entitySelectLabel}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <SelectField
          label={props.relationSelectLabel}
          value={relatedEntityData.relation || ''}
          onChange={handleRelationChange}
          fullWidth
        >
          {candidateRelations.map((relation: RelatedEntityTypes) => (
            <MenuItem value={relation}>
              {t(`relatedEntityTypes:${relation}`)}
            </MenuItem>
          ))}
        </SelectField>
      </Grid>
    </Grid>
  );
};

RelatedEntitySelect.defaultProps = {
  entitySelectLabel: i18n.t('relatedEntitySelect:defaultEntitySelectLabel'),
  relationSelectLabel: i18n.t('relatedEntitySelect:defaultRelationSelectLabel'),
};
